import React, { useMemo, useRef, useState } from "react";
import UIkit from "uikit";

const safeArray = (v) => (Array.isArray(v) ? v : []);

export default function InventoryAddPhotosOffcanvas({
  refId = "inventory-add-photos",
  propertyLabel = "",
  disabled = false,
  onSave,
}) {
  const offRef = useRef(null);

  const [filesToAdd, setFilesToAdd] = useState([]);
  const [urlToAdd, setUrlToAdd] = useState("");
  const [labelToAdd, setLabelToAdd] = useState("");
  const [categoryToAdd, setCategoryToAdd] = useState("Uncategorized");
  const [conditionToAdd, setConditionToAdd] = useState("Good");
  const [quantityToAdd, setQuantityToAdd] = useState(1);

  const close = () => {
    try {
      UIkit.offcanvas(`#${refId}`)?.hide();
    } catch {}
  };

  const buildMeta = () => ({
    label: labelToAdd.trim() || "Untitled",
    category: categoryToAdd,
    condition: conditionToAdd,
    quantity: Number(quantityToAdd) || 1,
    uploadedAt: new Date().toISOString(), // store ISO, format in UI
  });

  const onAddUrl = () => {
    if (disabled) return;

    const url = urlToAdd.trim();
    if (!url) {
      UIkit.notification({ message: "Paste a photo URL first.", status: "warning" });
      return;
    }

    const photo = {
      id: `ph_${Date.now()}`,
      url,
      ...buildMeta(),
    };

    if (typeof onSave === "function") onSave([photo]);

    setUrlToAdd("");
    UIkit.notification({ message: "Photo queued.", status: "success" });
    close();
  };

  const onAddFiles = () => {
    if (disabled) return;

    if (!filesToAdd.length) {
      UIkit.notification({ message: "Choose one or more files first.", status: "warning" });
      return;
    }

    const meta = buildMeta();

    const photos = Array.from(filesToAdd).map((f) => ({
      id: `ph_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      url: URL.createObjectURL(f), // local preview
      label: f.name, // file name overrides label
      category: meta.category,
      condition: meta.condition,
      quantity: meta.quantity,
      uploadedAt: meta.uploadedAt,
      _local: true,
    }));

    if (typeof onSave === "function") onSave(photos);

    setFilesToAdd([]);
    UIkit.notification({ message: "Photos added (local preview).", status: "success" });
    close();
  };

  return (
    <div
      id={refId}
      ref={offRef}
      className="hms-modals uk-offcanvas"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <button className="uk-offcanvas-close uk-close-large" type="button" uk-close="true" />

        <div className="hms-modals-main">
          <div className="uk-modal-header">
            <h2 className="uk-modal-title">Add Photos</h2>
            <div className="uk-text-meta">{propertyLabel || "Select a property first"}</div>
          </div>

          <div className="uk-modal-body">
            {/* Metadata (applies to whatever they add) */}
            <div className="uk-form-stacked">
              <div className="uk-margin">
                <label className="uk-form-label">Category</label>
                <input
                  className="uk-input"
                  value={categoryToAdd}
                  onChange={(e) => setCategoryToAdd(e.target.value)}
                  placeholder="Appliances, Furniture, Kitchen, Outdoor…"
                  disabled={disabled}
                />
              </div>

              <div className="uk-margin">
                <label className="uk-form-label">Condition</label>
                <select
                  className="uk-select"
                  value={conditionToAdd}
                  onChange={(e) => setConditionToAdd(e.target.value)}
                  disabled={disabled}
                >
                  <option value="New">New</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Needs Repair">Needs Repair</option>
                  <option value="Replace">Replace</option>
                </select>
              </div>

              <div className="uk-margin">
                <label className="uk-form-label">Quantity</label>
                <input
                  className="uk-input"
                  type="number"
                  min="1"
                  value={quantityToAdd}
                  onChange={(e) => setQuantityToAdd(e.target.value)}
                  disabled={disabled}
                />
              </div>

              <hr className="uk-margin" />

              {/* Upload files */}
              <div className="uk-margin">
                <div className="uk-text-bold">Upload files</div>
                <div className="uk-text-meta uk-margin-xsmall-bottom">
                  Choose one or more photos (coffee maker, knife drawer, etc.).
                </div>

                <input
                  className="uk-input uk-form-small"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setFilesToAdd(e.target.files ? Array.from(e.target.files) : [])}
                  disabled={disabled}
                />

                <div className="uk-flex uk-flex-between uk-flex-middle uk-margin-small-top">
                  <div className="uk-text-meta uk-text-small">
                    {filesToAdd.length ? `${filesToAdd.length} selected` : "No files selected"}
                  </div>
                  <button
                    type="button"
                    className="uk-button hms-btn hms-red-btn"
                    onClick={onAddFiles}
                    disabled={disabled}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}